// src/components/Hero.js
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  Reveal,
  MagneticButton,
  AnimatedCounter,
  GradientMesh,
  staggerContainer,
  staggerItem,
} from './ui/Motion';

const headlineWords = [
  { text: 'I', grad: false },
  { text: 'build', grad: false },
  { text: 'growth', grad: true },
  { text: 'systems', grad: true },
  { text: 'that', grad: false },
  { text: 'think.', grad: false },
];

const stats = [
  { value: 20, prefix: '$', suffix: 'M+', label: 'Ad spend managed' },
  { value: 400, suffix: '%+', label: 'CPL reduction' },
  { value: 1000, suffix: '%+', label: 'ROAS lift' },
  { value: 4, suffix: '', label: 'AI products shipped' },
];

const Hero = () => {
  return (
    <HeroSection id="home">
      <GradientMesh
        blobs={[
          { size: '46vw', top: '-8%', left: '-6%', color: '#6366f1', opacity: 0.4, blur: 110, dur: 20 },
          { size: '38vw', top: '8%', right: '-4%', color: '#ec4899', opacity: 0.32, blur: 100, dur: 24, delay: 3 },
          { size: '34vw', bottom: '-12%', left: '18%', color: '#06b6d4', opacity: 0.28, blur: 120, dur: 26, delay: 6 },
          { size: '22vw', top: '38%', left: '42%', color: '#8b5cf6', opacity: 0.25, blur: 90, dur: 22, delay: 1 },
        ]}
      />
      <GridOverlay aria-hidden="true" />

      <Container>
        <Content>
          <Badge
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Pulse />
            Growth &amp; Marketing Leader · AI Product Builder
          </Badge>

          <Title
            as={motion.h1}
            variants={staggerContainer(0.12, 0.15)}
            initial="hidden"
            animate="visible"
          >
            {headlineWords.map((w, i) => (
              <Word key={i} variants={staggerItem} className={w.grad ? 'grad-animate' : undefined}>
                {w.text}
              </Word>
            ))}
          </Title>

          <Reveal delay={0.5} as="div">
            <Description className="mobile-text-container">
              I design and scale high-performance marketing ecosystems across Search,
              Social, Native, and Programmatic — then wire them into{' '}
              <Hl>AI systems</Hl>: voice agents, CRM automation, and data pipelines
              that turn campaigns into revenue engines.
              <Tagline>If it connects audiences to results, I build it.</Tagline>
            </Description>
          </Reveal>

          <Reveal delay={0.65}>
            <ButtonGroup>
              <PrimaryButton as={MagneticButton} strength={0.35} onClick={() => scrollTo('work')}>
                <span>See my work</span>
                <Arrow />
              </PrimaryButton>
              <SecondaryButton as={MagneticButton} strength={0.35} onClick={() => scrollTo('voice-agent')}>
                <span>Meet my AI agent</span>
                <WaveIcon />
              </SecondaryButton>
            </ButtonGroup>
          </Reveal>

          <StatStrip
            as={motion.div}
            variants={staggerContainer(0.1, 0.8)}
            initial="hidden"
            animate="visible"
          >
            {stats.map((s) => (
              <Stat key={s.label} variants={staggerItem}>
                <StatNum className="grad-animate">
                  <AnimatedCounter value={s.value} prefix={s.prefix} suffix={s.suffix} />
                </StatNum>
                <StatLabel>{s.label}</StatLabel>
              </Stat>
            ))}
          </StatStrip>
        </Content>
      </Container>

      <ScrollHint
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.6 }}
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M12 5V19M12 19L5 12M12 19L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </motion.div>
        <span>Scroll</span>
      </ScrollHint>
    </HeroSection>
  );
};

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

const Arrow = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WaveIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M2 12h2M7 6v12M12 3v18M17 6v12M20 10v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ---------------- styles ---------------- */

const HeroSection = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  overflow: hidden;
  padding: 11rem 0 6rem;
  background: radial-gradient(ellipse at 50% -10%, rgba(99,102,241,0.15), transparent 55%),
              ${({ theme }) => theme.colors.dark};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 9rem 0 4rem;
  }
`;

const GridOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  background-image:
    linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
  background-size: 64px 64px;
  mask-image: radial-gradient(ellipse at 50% 40%, #000 30%, transparent 75%);
  -webkit-mask-image: radial-gradient(ellipse at 50% 40%, #000 30%, transparent 75%);
`;

const Container = styled.div`
  max-width: 1120px;
  width: 100%;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 2;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0 1.5rem;
  }
`;

const Content = styled.div`
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
`;

const Badge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid ${({ theme }) => theme.colors.border};
  backdrop-filter: blur(10px);
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2rem;
`;

const Pulse = styled.span`
  position: relative;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 10px #10b981;

  &::after {
    content: '';
    position: absolute;
    inset: -3px;
    border-radius: 50%;
    border: 1px solid #10b981;
    animation: pulseRing 2s ease-out infinite;
  }
`;

const Title = styled.h1`
  font-size: clamp(2.6rem, 7vw, 5.2rem);
  font-weight: 800;
  line-height: 1.02;
  letter-spacing: -0.03em;
  margin-bottom: 1.75rem;
`;

const Word = styled(motion.span)`
  display: inline-block;
  margin-right: 0.28em;

  &:last-child {
    margin-right: 0;
  }
`;

const Description = styled.div`
  max-width: 640px;
  margin: 0 auto 2.5rem;
  font-size: 1.2rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.05rem;
  }
`;

const Hl = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-weight: 600;
  background: linear-gradient(120deg, rgba(139,92,246,0.25), rgba(236,72,153,0.25));
  padding: 0.05em 0.3em;
  border-radius: 6px;
`;

const Tagline = styled.p`
  margin-top: 1.5rem;
  font-weight: 700;
  font-size: 1.15rem;
  color: ${({ theme }) => theme.colors.text};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 4rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const btnBase = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 1rem 1.9rem;
  font-weight: 600;
  font-size: 1rem;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
`;

const PrimaryButton = styled.button`
  ${btnBase}
  color: #fff;
  position: relative;
  background: linear-gradient(120deg, #6366f1, #8b5cf6, #ec4899);
  background-size: 200% 100%;
  box-shadow: 0 8px 30px rgba(99, 102, 241, 0.4);
  transition: background-position 0.5s ease, box-shadow 0.3s ease;

  &:hover {
    background-position: 100% 0;
    box-shadow: 0 12px 40px rgba(236, 72, 153, 0.5);
  }
  svg { transition: transform 0.3s ease; }
  &:hover svg { transform: translateX(4px); }
`;

const SecondaryButton = styled.button`
  ${btnBase}
  color: ${({ theme }) => theme.colors.text};
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  backdrop-filter: blur(10px);
  transition: background 0.3s ease, border-color 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(6, 182, 212, 0.5);
  }
  svg { color: #06b6d4; }
`;

const StatStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  padding: 1.75rem;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid ${({ theme }) => theme.colors.border};
  backdrop-filter: blur(12px);

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
`;

const Stat = styled(motion.div)`
  text-align: center;
`;

const StatNum = styled.div`
  font-size: clamp(1.6rem, 3vw, 2.4rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1;
`;

const StatLabel = styled.div`
  margin-top: 0.5rem;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const ScrollHint = styled(motion.div)`
  position: absolute;
  bottom: 1.75rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.7rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  z-index: 2;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: none;
  }
`;

export default Hero;
